import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SearchDrawer = ({ isOpen, onClose, userRole }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tradies');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const isCompanyMember = userRole === 'company_member';

  useEffect(() => {
    if (query.trim().length >= 2) {
      const timer = setTimeout(() => fetchResults(), 300);
      return () => clearTimeout(timer);
    } else {
      setResults([]);
    }
  }, [query, activeTab]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/search?scope=${activeTab}&q=${encodeURIComponent(query)}&limit=5`);
      setResults(response.data.results || []);
    } catch (error) {
      console.error(error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!query.trim()) return;
    
    switch (activeTab) {
      case 'tradies':
        navigate(`/tradies/browse?q=${encodeURIComponent(query)}`);
        break;
      case 'jobs':
        navigate(`/jobs/browse?search=${encodeURIComponent(query)}`);
        break;
      case 'talent':
        navigate(`/portal/talent?keyword=${encodeURIComponent(query)}`);
        break;
      case 'posts':
      case 'news':
        break;
      default:
        break;
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center pt-20" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl mx-4 rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-xl font-bold">Find</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="flex gap-2 mb-4 border-b overflow-x-auto">
            <button onClick={() => setActiveTab('tradies')} className={`px-4 py-2 whitespace-nowrap ${activeTab === 'tradies' ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
              Tradies
            </button>
            <button onClick={() => setActiveTab('jobs')} className={`px-4 py-2 whitespace-nowrap ${activeTab === 'jobs' ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
              Jobs
            </button>
            <button onClick={() => setActiveTab('posts')} className={`px-4 py-2 whitespace-nowrap ${activeTab === 'posts' ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
              Posts
            </button>
            <button onClick={() => setActiveTab('news')} className={`px-4 py-2 whitespace-nowrap ${activeTab === 'news' ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
              News
            </button>
            {isCompanyMember && (
              <button onClick={() => setActiveTab('talent')} className={`px-4 py-2 whitespace-nowrap ${activeTab === 'talent' ? 'border-b-2 border-blue-600 text-blue-600 font-semibold' : 'text-muted-foreground'}`}>
                Talent
              </button>
            )}
          </div>
          
          <div className="flex gap-2 mb-4">
            <Input
              placeholder={`Search ${activeTab}...`}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              autoFocus
            />
            <Button onClick={handleSearch}>See All</Button>
          </div>
          
          {/* Live Results */}
          {loading && <div className="text-center py-4 text-muted-foreground">Searching...</div>}
          {!loading && results.length > 0 && (
            <div className="space-y-2">
              {results.map((result, idx) => (
                <div key={idx} onClick={() => { navigate(result.url); onClose(); }} className="p-3 border rounded hover:bg-muted cursor-pointer">
                  <h4 className="font-semibold text-sm">{result.title}</h4>
                  <p className="text-xs text-muted-foreground">{result.subtitle}</p>
                </div>
              ))}
            </div>
          )}
          {!loading && query.trim().length >= 2 && results.length === 0 && (
            <div className="text-center py-4 text-muted-foreground">No results found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchDrawer;
